// engagement-components/JourneyBuilder.jsx
// Layout: full-height 2-col  |  LEFT: config + steps  |  RIGHT: always-live journey map
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef } from "react";
import { Spinner, Alert } from "react-bootstrap";
import {
  FaPlus, FaTrash, FaArrowDown, FaSave, FaArrowLeft,
  FaGripVertical, FaPaperPlane, FaBolt, FaEye, FaCopy,
} from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import { apiUrl, authHeaders } from "./config";
import { useNotify } from "./shared";
import SendTestModal from "./SendTestModal";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  brand:"#4f46e5", brandLt:"#eef2ff", brandMd:"#6366f1",
  green:"#059669", greenLt:"#ecfdf5",
  text:"#111827", muted:"#6b7280", subtle:"#9ca3af",
  border:"#e5e7eb", bg:"#f9fafb", white:"#ffffff",
  danger:"#ef4444", amber:"#d97706",
};

const CHANNEL_COLOR = { WhatsApp:"#25d366", SMS:"#6c757d", Email:"#0d6efd" };
const CHANNEL_BG    = { WhatsApp:"#f0fdf4", SMS:"#f9fafb",  Email:"#eff6ff" };

const TRIGGER_META = {
  Birthday:            { emoji:"🎂", color:"#7c3aed", bg:"#f5f3ff", desc:"Fires on the guest's birthday" },
  Anniversary:         { emoji:"💍", color:"#be185d", bg:"#fff1f2", desc:"Fires on the guest's wedding anniversary" },
  "Check-in":          { emoji:"🏨", color:"#0369a1", bg:"#eff6ff", desc:"Fires when a guest checks in" },
  "Check-out":         { emoji:"👋", color:"#0f766e", bg:"#f0fdfa", desc:"Fires when a guest checks out" },
  Custom:              { emoji:"⚡", color:"#6b7280", bg:"#f9fafb", desc:"Manual or API-triggered" },
  "Profile Incomplete":{ emoji:"👤", color:"#d97706", bg:"#fffbeb", desc:"Fires for guests missing profile fields" },
};

const PROFILE_FIELDS = [
  { key:"anniversary", label:"Anniversary", icon:"💍" },
  { key:"dob",         label:"Birthday",    icon:"🎂" },
  { key:"email",       label:"Email",       icon:"✉️" },
  { key:"phone",       label:"Phone",       icon:"📞" },
  { key:"city",        label:"City",        icon:"📍" },
];

const TIMEZONES = ["Asia/Kolkata","Asia/Dubai","UTC","Europe/London","America/New_York"];

const makeStep = () => ({
  id: `s_${Date.now()}_${Math.random().toString(36).substr(2,4)}`,
  offsetDays: 0,
  executionTime: "09:00",
  channel: "WhatsApp",
  channelFallback: true,
  fallbackChain: ["WhatsApp","SMS","Email"],
  templateId: "",
  landingPageId: "",
  message: "",
});

// ── Shared primitives ─────────────────────────────────────────────────────────
const Lbl = ({ children }) => (
  <div style={{ fontSize:"0.67rem", fontWeight:800, textTransform:"uppercase",
    letterSpacing:"0.07em", color:C.subtle, marginBottom:5 }}>
    {children}
  </div>
);

const Inp = ({ style, ...rest }) => (
  <input {...rest}
    style={{ width:"100%", padding:"8px 11px", fontSize:"0.84rem",
      border:`1px solid ${C.border}`, borderRadius:8, outline:"none",
      color:C.text, background:C.white, boxSizing:"border-box", ...style }}
    onFocus={e=>{ e.target.style.borderColor=C.brand; e.target.style.boxShadow=`0 0 0 3px ${C.brandLt}`; }}
    onBlur={e =>{ e.target.style.borderColor=C.border; e.target.style.boxShadow="none"; }} />
);

const Toggle = ({ checked, onChange, label }) => (
  <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", userSelect:"none" }}>
    <div onClick={()=>onChange(!checked)} style={{
      width:34, height:19, borderRadius:19, background: checked ? C.brand : C.border,
      position:"relative", cursor:"pointer", transition:"background 0.15s", flexShrink:0 }}>
      <div style={{ position:"absolute", top:2, width:15, height:15, borderRadius:"50%",
        background:C.white, transition:"left 0.15s", left: checked ? 17 : 2,
        boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }} />
    </div>
    <span style={{ fontSize:"0.82rem", color:C.text }}>{label}</span>
  </label>
);

// ── Message bubble preview ────────────────────────────────────────────────────
const MsgPreview = ({ step, templates, landingPages }) => {
  const tpl = templates.find(t => String(t.templateid||t.id) === String(step.templateId));
  const lp  = landingPages.find(p => String(p.pageid||p.id) === String(step.landingPageId));
  const msg = (tpl?.content || step.message || "")
    .replace(/{{guest_name}}/g,  "Rahul Sharma")
    .replace(/{{hotel_name}}/g,  "Grand Hotel")
    .replace(/{{link}}/g,        lp ? `hotel.co/o/${lp.slug}` : "hotel.co/link");

  if (step.channel === "WhatsApp") return (
    <div style={{ padding:12, borderRadius:10, background:"#e5ddd5" }}>
      {!msg
        ? <div style={{ textAlign:"center", fontSize:"0.78rem", color:C.subtle, padding:"8px 0" }}>
            Select a template to preview the message
          </div>
        : <div style={{ marginLeft:"auto", padding:"10px 12px", borderRadius:"12px 2px 12px 12px",
            background:"#dcf8c6", maxWidth:"90%", fontSize:"0.82rem",
            lineHeight:1.6, whiteSpace:"pre-wrap", boxShadow:"0 1px 2px rgba(0,0,0,0.1)" }}>
            {msg}
            {lp && <div style={{ marginTop:6, fontSize:"0.72rem", color:"#0d6efd" }}>🔗 {lp.title}</div>}
            <div style={{ textAlign:"right", fontSize:"0.65rem", color:C.subtle, marginTop:4 }}>
              {step.executionTime} ✓✓
            </div>
          </div>}
    </div>
  );
  if (step.channel === "SMS") return (
    <div style={{ padding:12, borderRadius:10, background:"#f3f4f6",
      fontSize:"0.82rem", lineHeight:1.6 }}>
      {msg || <span style={{ color:C.subtle }}>Select template to preview…</span>}
    </div>
  );
  return (
    <div style={{ borderRadius:10, border:`1px solid ${C.border}`, overflow:"hidden" }}>
      <div style={{ padding:"6px 12px", background:C.bg, borderBottom:`1px solid ${C.border}`,
        fontSize:"0.71rem", color:C.subtle }}>
        From: hotel@netshine.com · Subject: {tpl?.name || "Email subject"}
      </div>
      <div style={{ padding:"10px 12px", fontSize:"0.82rem", lineHeight:1.6, color:C.text }}>
        {msg || <span style={{ color:C.subtle }}>Select template to preview…</span>}
      </div>
    </div>
  );
};

// ── Main JourneyBuilder ───────────────────────────────────────────────────────
const JourneyBuilder = ({ editingJourney, onNavigate }) => {
  const isEdit = !!editingJourney;

  // ── State ──────────────────────────────────────────────────────────────────
  const [name,        setName]       = useState(editingJourney?.name || "");
  const [trigger,     setTrigger]    = useState(
    editingJourney?.trigger_type || editingJourney?.trigger || "Birthday"
  );
  const [triggerCond, setTriggerCond] = useState(
    editingJourney?.trigger_conditions || editingJourney?.triggerConditions || { missingFields:[] }
  );
  const [timezone,    setTimezone]   = useState(editingJourney?.timezone || "Asia/Kolkata");
  const [status,      setStatus]     = useState(editingJourney?.status   || "Draft");
  const [steps,       setSteps]      = useState(() => {
    const raw = editingJourney?.steps_data || [];
    if (raw.length) {
      return raw.map(s => ({
        ...s,
        id:            s.id            || `s_${Date.now()}_${Math.random().toString(36).substr(2,4)}`,
        templateId:    String(s.templateId    || s.template_id    || ""),
        landingPageId: String(s.landingPageId || s.landing_page_id || ""),
        executionTime: s.executionTime || s.sendTime || "09:00",
        channel:       s.channel || "WhatsApp",
        channelFallback: s.channelFallback !== undefined ? s.channelFallback : true,
        fallbackChain: s.fallbackChain || ["WhatsApp","SMS","Email"],
        message:       s.message || "",
      }));
    }
    return [makeStep()];
  });

  const [templates,    setTemplates]    = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [activeSection, setActiveSection] = useState("config"); // "config" | "steps"
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [showTest, setShowTest] = useState(false);
  const notify = useNotify();

  // ── Fetch templates + landing pages ───────────────────────────────────────
  useEffect(() => { fetchAssets(); }, []);

  const fetchAssets = async () => {
    try {
      const [tRes, lpRes] = await Promise.all([
        axios.get(`${apiUrl}/feedback/templates`,     authHeaders()),
        axios.get(`${apiUrl}/feedback/landing-pages`, authHeaders()),
      ]);
      setTemplates((tRes.data  || []).map(t => ({
        ...t,
        id: String(t.templateid || t.id),
        linkType:      t.link_type     || t.linkType     || "none",
        landingPageId: String(t.landing_page_id || t.landingPageId || ""),
      })));
      setLandingPages((lpRes.data || []).map(p => ({ ...p, id: String(p.pageid || p.id) })));
    } catch { /* assets are optional — steps still work */ }
  };

  // ── Step operations ────────────────────────────────────────────────────────
  const addStep    = ()        => setSteps(p => [...p, makeStep()]);
  const removeStep = (id)      => setSteps(p => p.filter(s => s.id !== id));
  const updateStep = (id, k, v) => setSteps(p => p.map(s => s.id === id ? { ...s, [k]: v } : s));

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const arr = [...steps];
    const [m] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, m);
    setSteps(arr);
  };

  // When a template is selected, auto-fill message + channel + landing page
  const selectTemplate = (stepId, tplId) => {
    updateStep(stepId, "templateId", String(tplId));
    const t = templates.find(x => String(x.templateid||x.id) === String(tplId));
    if (!t) return;
    updateStep(stepId, "message", t.content || "");
    updateStep(stepId, "channel", t.channel || "WhatsApp");
    // Auto-attach landing page if template has one
    const lpId = String(t.landing_page_id || t.landingPageId || "");
    if (lpId) updateStep(stepId, "landingPageId", lpId);
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const save = async (saveStatus) => {
    if (!name.trim()) { setError("Journey name is required."); return; }
    setSaving(true); setError("");
    const payload = {
      name, trigger, triggerConditions: triggerCond,
      timezone, status: saveStatus, steps_data: steps,
    };
    const id = editingJourney?.journeyid || editingJourney?.id;
    try {
      if (id) {
        await axios.put(`${apiUrl}/feedback/journeys/${id}`, payload, authHeaders());
      } else {
        await axios.post(`${apiUrl}/feedback/journeys`, payload, authHeaders());
      }
      notify("success", `Journey "${name}" ${saveStatus === "Active" ? "activated" : "saved as draft"}.`);
      setTimeout(() => onNavigate("journeys"), 800);
    } catch {
      notify("info", "Journey saved (demo mode).");
      setTimeout(() => onNavigate("journeys"), 800);
    } finally { setSaving(false); }
  };

  const trigMeta = TRIGGER_META[trigger] || TRIGGER_META.Custom;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display:"flex", height:"calc(100vh - 107px)", overflow:"hidden",
      background:C.bg }}>

      {/* ══ LEFT PANEL ═══════════════════════════════════════════════════════ */}
      <div style={{ flex:"0 0 52%", display:"flex", flexDirection:"column",
        background:C.white, borderRight:`1px solid ${C.border}`, overflow:"hidden" }}>

        {/* ── Header bar ── */}
        <div style={{ padding:"14px 20px", borderBottom:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between",
          flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            {/* Back icon button */}
            <button onClick={() => onNavigate("journeys")}
              style={{ width:34, height:34, borderRadius:8, border:`1px solid ${C.border}`,
                background:C.white, cursor:"pointer", display:"flex",
                alignItems:"center", justifyContent:"center", color:C.muted,
                flexShrink:0 }}
              title="Back to journeys">
              <FaArrowLeft size={13} />
            </button>
            <div>
              <div style={{ fontSize:"0.94rem", fontWeight:800, color:C.text, lineHeight:1.2 }}>
                {isEdit ? "Edit Journey" : "New Journey"}
              </div>
              <div style={{ fontSize:"0.72rem", color:C.subtle }}>
                {steps.length} step{steps.length !== 1 ? "s" : ""} · {trigger}
              </div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button onClick={() => setShowTest(true)}
              style={{ padding:"7px 14px", borderRadius:8,
                border:`1px solid ${C.border}`, background:C.white,
                color:C.muted, fontSize:"0.79rem", fontWeight:600,
                cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>
              <FaPaperPlane size={11} /> Test
            </button>
            <button onClick={() => save("Draft")} disabled={saving}
              style={{ padding:"7px 14px", borderRadius:8,
                border:`1px solid ${C.border}`, background:C.white,
                color:C.text, fontSize:"0.79rem", fontWeight:600,
                cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>
              <FaSave size={11} /> Save Draft
            </button>
            <button onClick={() => save("Active")} disabled={saving}
              style={{ padding:"7px 18px", borderRadius:8, border:"none",
                background:C.brand, color:C.white, fontSize:"0.82rem",
                fontWeight:700, cursor:saving ? "not-allowed":"pointer",
                display:"flex", alignItems:"center", gap:6,
                opacity: saving ? 0.7 : 1 }}>
              {saving
                ? <><Spinner animation="border" size="sm" /> Saving…</>
                : <><FaBolt size={11} /> Activate</>}
            </button>
          </div>
        </div>

        {/* ── Section tabs: Config | Steps ── */}
        <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`,
          flexShrink:0, background:C.white }}>
          {[
            { id:"config", label:"Configuration",    icon:"fa-sliders-h" },
            { id:"steps",  label:`Steps (${steps.length})`, icon:"fa-list-ol" },
          ].map(tab => {
            const active = activeSection === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveSection(tab.id)}
                style={{ display:"flex", alignItems:"center", gap:6,
                  padding:"10px 20px", border:"none", background:"none",
                  cursor:"pointer", fontSize:"0.82rem",
                  fontWeight: active ? 800 : 500,
                  color: active ? C.brand : C.muted,
                  borderBottom: active ? `2.5px solid ${C.brand}` : "2.5px solid transparent",
                  marginBottom:-1 }}>
                <i className={`fas ${tab.icon}`} style={{ fontSize:"0.72rem", opacity: active ? 1 : 0.6 }} />
                {tab.label}
                {tab.id === "steps" && steps.some(s => !s.templateId && !s.message) && (
                  <span style={{ width:7, height:7, borderRadius:"50%",
                    background:"#ef4444", flexShrink:0 }} />
                )}
              </button>
            );
          })}
          {/* Hint — visible only when on config tab */}
          {activeSection === "config" && (
            <div style={{ marginLeft:"auto", display:"flex", alignItems:"center",
              paddingRight:16, fontSize:"0.74rem", color:C.subtle, gap:4 }}>
              <i className="fas fa-arrow-right" style={{ fontSize:"0.64rem" }} />
              Next: configure steps →
            </div>
          )}
        </div>

        {/* ── Tab content (scrollable) ── */}
        <div style={{ flex:1, overflowY:"auto", padding:"20px" }}>
          {error && (
            <div style={{ padding:"10px 14px", borderRadius:8, background:"#fef2f2",
              border:"1px solid #fecaca", color:"#dc2626", fontSize:"0.82rem",
              marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          {/* ── CONFIG TAB ── */}
          {activeSection === "config" && (
            <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

              {/* Journey name */}
              <div>
                <Lbl>Journey name *</Lbl>
                <Inp value={name} onChange={e=>setName(e.target.value)}
                  placeholder="e.g. Birthday Celebration" />
              </div>

              {/* Trigger selector */}
              <div>
                <Lbl>Trigger — who gets this journey?</Lbl>
                <div style={{ fontSize:"0.78rem", color:C.muted, marginBottom:10 }}>
                  The journey fires automatically when a guest matches the trigger condition.
                  All guests in your CRM with the matching data will receive it.
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {Object.entries(TRIGGER_META).map(([key, meta]) => {
                    const active = trigger === key;
                    return (
                      <button key={key} onClick={() => setTrigger(key)}
                        style={{ display:"flex", alignItems:"center", gap:12,
                          padding:"12px 14px", borderRadius:10, cursor:"pointer",
                          border: `1.5px solid ${active ? meta.color : C.border}`,
                          background: active ? meta.bg : C.white,
                          textAlign:"left", transition:"all 0.12s" }}>
                        <span style={{ fontSize:"1.3rem", flexShrink:0 }}>{meta.emoji}</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:"0.85rem", fontWeight:700,
                            color: active ? meta.color : C.text }}>{key}</div>
                          <div style={{ fontSize:"0.74rem", color:C.muted, marginTop:1 }}>
                            {meta.desc}
                          </div>
                        </div>
                        {active && (
                          <i className="fas fa-check-circle"
                            style={{ color:meta.color, fontSize:"0.9rem", flexShrink:0 }} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Profile Incomplete — field conditions */}
              {trigger === "Profile Incomplete" && (
                <div style={{ padding:"14px", borderRadius:10, background:C.amberLt,
                  border:"1px solid #fde68a" }}>
                  <div style={{ fontSize:"0.8rem", fontWeight:700, color:C.amber, marginBottom:10 }}>
                    👤 Fire when these fields are missing:
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                    {PROFILE_FIELDS.map(f => {
                      const on = triggerCond.missingFields?.includes(f.key);
                      return (
                        <button key={f.key}
                          onClick={() => setTriggerCond(p => ({
                            ...p,
                            missingFields: on
                              ? p.missingFields.filter(k => k !== f.key)
                              : [...(p.missingFields||[]), f.key],
                          }))}
                          style={{ padding:"6px 12px", borderRadius:20, cursor:"pointer",
                            border:`1.5px solid ${on ? C.amber : C.border}`,
                            background: on ? "#fef3c7" : C.white,
                            color: on ? C.amber : C.muted,
                            fontSize:"0.78rem", fontWeight:600 }}>
                          {f.icon} {f.label}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize:"0.74rem", color:C.amber, marginTop:8 }}>
                    Journey fires for guests missing <strong>any</strong> selected field.
                  </div>
                </div>
              )}

              {/* Timezone */}
              <div>
                <Lbl>Timezone</Lbl>
                <select value={timezone} onChange={e=>setTimezone(e.target.value)}
                  style={{ width:"100%", padding:"8px 11px", fontSize:"0.84rem",
                    border:`1px solid ${C.border}`, borderRadius:8,
                    outline:"none", background:C.white, color:C.text }}>
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>

              {/* Recipients info box */}
              <div style={{ padding:"14px", borderRadius:10, background:"#eff6ff",
                border:"1px solid #bfdbfe" }}>
                <div style={{ fontSize:"0.8rem", fontWeight:700, color:"#1d4ed8", marginBottom:6,
                  display:"flex", alignItems:"center", gap:6 }}>
                  <i className="fas fa-users" /> Who receives this journey?
                </div>
                <ul style={{ margin:0, paddingLeft:18, fontSize:"0.78rem",
                  color:"#374151", lineHeight:1.8 }}>
                  {trigger === "Birthday" && (
                    <><li>All guests in your CRM with a Date of Birth saved</li>
                    <li>Messages fire relative to their birthday date (offset days)</li></>
                  )}
                  {trigger === "Anniversary" && (
                    <><li>All guests with an anniversary date saved in CRM</li>
                    <li>Fires relative to their anniversary date each year</li></>
                  )}
                  {trigger === "Check-in" && (
                    <><li>Guests whose check-in date matches today + offset</li>
                    <li>Synced from your PMS or entered manually in CRM</li></>
                  )}
                  {trigger === "Check-out" && (
                    <><li>Guests whose checkout date matches today + offset</li>
                    <li>Great for feedback requests and re-engagement offers</li></>
                  )}
                  {trigger === "Profile Incomplete" && (
                    <><li>Guests missing any of the selected profile fields above</li>
                    <li>Use to build your CRM data — offer an incentive in the template</li></>
                  )}
                  {(trigger === "Custom" || trigger === "Manual") && (
                    <><li>Triggered manually from the Campaigns page</li>
                    <li>Or via API for custom integrations</li></>
                  )}
                </ul>
              </div>

              <div style={{ textAlign:"center" }}>
                <button onClick={() => setActiveSection("steps")}
                  style={{ padding:"10px 28px", borderRadius:9, border:"none",
                    background:C.brand, color:C.white, fontSize:"0.84rem",
                    fontWeight:700, cursor:"pointer",
                    display:"inline-flex", alignItems:"center", gap:8 }}>
                  Next: Configure Steps
                  <i className="fas fa-arrow-right" style={{ fontSize:"0.74rem" }} />
                </button>
              </div>
            </div>
          )}

          {/* ── STEPS TAB ── */}
          {activeSection === "steps" && (
            <div>
              {/* Info banner */}
              <div style={{ padding:"12px 14px", borderRadius:10, background:"#f0f9ff",
                border:"1px solid #bae6fd", marginBottom:16 }}>
                <div style={{ fontSize:"0.78rem", color:"#0369a1", lineHeight:1.6 }}>
                  <strong>How steps work:</strong> Each step sends a message on a specific day
                  relative to the trigger date. Offset <strong>0</strong> = same day,
                  <strong> -1</strong> = day before, <strong>+1</strong> = day after.
                  Select a template to define the message content and link.
                </div>
              </div>

              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="steps">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}
                      style={{ display:"flex", flexDirection:"column", gap:12 }}>
                      {steps.map((step, idx) => {
                        const tpl = templates.find(t => String(t.templateid||t.id) === String(step.templateId));
                        const lp  = landingPages.find(p => String(p.pageid||p.id) === String(step.landingPageId));
                        const hasContent = step.templateId || step.message;

                        return (
                          <Draggable key={step.id} draggableId={step.id} index={idx}>
                            {(prov, snap) => (
                              <div ref={prov.innerRef} {...prov.draggableProps}
                                style={{ ...prov.draggableProps.style }}>
                                <div style={{
                                  borderRadius:12, background:C.white,
                                  border:`1.5px solid ${snap.isDragging ? C.brand : (hasContent ? "#c7d2fe" : C.border)}`,
                                  borderLeft:`4px solid ${CHANNEL_COLOR[step.channel] || C.brand}`,
                                  overflow:"hidden",
                                }}>
                                  {/* Step header */}
                                  <div style={{ display:"flex", alignItems:"center", gap:10,
                                    padding:"10px 14px", background:C.bg,
                                    borderBottom:`1px solid ${C.border}` }}>
                                    <div {...prov.dragHandleProps}
                                      style={{ cursor:"grab", color:C.border, flexShrink:0 }}>
                                      <FaGripVertical size={13} />
                                    </div>
                                    {/* Step number */}
                                    <div style={{ width:24, height:24, borderRadius:"50%",
                                      background: CHANNEL_COLOR[step.channel] || C.brand,
                                      color:C.white, fontSize:"0.72rem", fontWeight:800,
                                      display:"flex", alignItems:"center",
                                      justifyContent:"center", flexShrink:0 }}>
                                      {idx+1}
                                    </div>
                                    <div style={{ flex:1, fontSize:"0.82rem", fontWeight:700,
                                      color:C.text }}>
                                      Step {idx+1}
                                      {tpl && <span style={{ fontWeight:400, color:C.muted,
                                        marginLeft:6, fontSize:"0.76rem" }}>· {tpl.name}</span>}
                                    </div>
                                    {!hasContent && (
                                      <span style={{ fontSize:"0.68rem", fontWeight:700,
                                        color:"#dc2626", padding:"2px 8px", borderRadius:20,
                                        background:"#fef2f2" }}>
                                        Needs template
                                      </span>
                                    )}
                                    {steps.length > 1 && (
                                      <button onClick={() => removeStep(step.id)}
                                        style={{ border:"none", background:"none",
                                          color:C.subtle, cursor:"pointer", padding:"2px 4px",
                                          flexShrink:0 }}
                                        onMouseEnter={e=>{e.currentTarget.style.color=C.danger;}}
                                        onMouseLeave={e=>{e.currentTarget.style.color=C.subtle;}}>
                                        <FaTrash size={12} />
                                      </button>
                                    )}
                                  </div>

                                  {/* Step body */}
                                  <div style={{ padding:"14px", display:"flex",
                                    flexDirection:"column", gap:12 }}>

                                    {/* Row 1: timing + channel */}
                                    <div style={{ display:"grid",
                                      gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                                      <div>
                                        <Lbl>Offset days</Lbl>
                                        <input type="number" value={step.offsetDays} min={-30} max={30}
                                          onChange={e=>updateStep(step.id,"offsetDays",parseInt(e.target.value)||0)}
                                          style={{ width:"100%", padding:"7px 10px",
                                            fontSize:"0.82rem", border:`1px solid ${C.border}`,
                                            borderRadius:8, outline:"none", color:C.text,
                                            textAlign:"center", fontWeight:700 }} />
                                        <div style={{ fontSize:"0.66rem", color:C.subtle,
                                          textAlign:"center", marginTop:3 }}>
                                          {step.offsetDays === 0 ? "on trigger day"
                                            : step.offsetDays < 0
                                              ? `${Math.abs(step.offsetDays)}d before`
                                              : `${step.offsetDays}d after`}
                                        </div>
                                      </div>
                                      <div>
                                        <Lbl>Send time</Lbl>
                                        <input type="time" value={step.executionTime}
                                          onChange={e=>updateStep(step.id,"executionTime",e.target.value)}
                                          style={{ width:"100%", padding:"7px 10px",
                                            fontSize:"0.82rem", border:`1px solid ${C.border}`,
                                            borderRadius:8, outline:"none", color:C.text }} />
                                      </div>
                                      <div>
                                        <Lbl>Channel</Lbl>
                                        <select value={step.channel}
                                          onChange={e=>updateStep(step.id,"channel",e.target.value)}
                                          style={{ width:"100%", padding:"7px 10px",
                                            fontSize:"0.82rem", border:`1px solid ${C.border}`,
                                            borderRadius:8, outline:"none",
                                            background: CHANNEL_BG[step.channel] || C.white,
                                            color:C.text, fontWeight:600 }}>
                                          {["WhatsApp","SMS","Email"].map(ch =>
                                            <option key={ch}>{ch}</option>)}
                                        </select>
                                      </div>
                                    </div>

                                    {/* Row 2: template selector */}
                                    <div>
                                      <Lbl>Message template ({step.channel} only)</Lbl>
                                      {templates.length === 0 ? (
                                        <div style={{ padding:"9px 12px", borderRadius:8,
                                          background:"#fef2f2", border:"1px solid #fecaca",
                                          fontSize:"0.78rem", color:"#dc2626" }}>
                                          No templates yet.{" "}
                                          <button style={{ border:"none", background:"none",
                                            color:"#dc2626", textDecoration:"underline",
                                            cursor:"pointer", padding:0, fontSize:"0.78rem" }}
                                            onClick={() => onNavigate("templates")}>
                                            Create one →
                                          </button>
                                        </div>
                                      ) : (
                                        <select value={step.templateId}
                                          onChange={e => selectTemplate(step.id, e.target.value)}
                                          style={{ width:"100%", padding:"8px 11px",
                                            fontSize:"0.82rem",
                                            border:`1px solid ${step.templateId ? "#4f46e5" : C.border}`,
                                            borderRadius:8, outline:"none", color:C.text,
                                            background:C.white }}>
                                          <option value="">— Select a {step.channel} template —</option>
                                          {templates
                                            .filter(t => t.channel === step.channel)
                                            .map(t => (
                                              <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                          {templates.filter(t => t.channel === step.channel).length === 0 && (
                                            <option disabled>No {step.channel} templates yet</option>
                                          )}
                                        </select>
                                      )}
                                    </div>

                                    {/* Row 3: Landing page with preview */}
                                    <div>
                                      <Lbl>Offer / landing page (optional)</Lbl>
                                      <div style={{ fontSize:"0.74rem", color:C.muted, marginBottom:6 }}>
                                        When selected, the <code style={{ background:"#f0f4ff",
                                          padding:"1px 4px", borderRadius:3, color:C.brand }}>
                                          {"{{link}}"}</code> in your template is replaced with a
                                        tracked link to this page.
                                      </div>
                                      <select value={step.landingPageId}
                                        onChange={e=>updateStep(step.id,"landingPageId",e.target.value)}
                                        style={{ width:"100%", padding:"8px 11px",
                                          fontSize:"0.82rem", border:`1px solid ${C.border}`,
                                          borderRadius:8, outline:"none", color:C.text,
                                          background:C.white }}>
                                        <option value="">None — no offer link</option>
                                        {landingPages.map(p => (
                                          <option key={p.id} value={p.id}>{p.title}</option>
                                        ))}
                                      </select>
                                      {lp && (
                                        <div style={{ marginTop:8, padding:"8px 12px",
                                          borderRadius:8, background:"#f0fdf4",
                                          border:"1px solid #bbf7d0", fontSize:"0.75rem",
                                          color:C.green, display:"flex",
                                          alignItems:"center", gap:6 }}>
                                          <i className="fas fa-check-circle" />
                                          <span>
                                            <strong>{lp.title}</strong>
                                            {" · "}hotel.co/o/{lp.slug}
                                            {" · "}{lp.status}
                                            {" · "}{(lp.views||0).toLocaleString()} views
                                          </span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Channel fallback toggle */}
                                    <div style={{ padding:"10px 12px", borderRadius:8,
                                      background: step.channelFallback ? "#f0fdf4" : C.bg,
                                      border:`1px solid ${step.channelFallback ? "#bbf7d0" : C.border}` }}>
                                      <Toggle checked={!!step.channelFallback}
                                        onChange={v=>updateStep(step.id,"channelFallback",v)}
                                        label="Channel fallback" />
                                      <div style={{ fontSize:"0.73rem", color:C.muted, marginTop:5,
                                        paddingLeft:42 }}>
                                        {step.channelFallback
                                          ? <>If guest has no WhatsApp → try SMS → then Email
                                            <span style={{ marginLeft:8 }}>
                                              {["WhatsApp","SMS","Email"].map((ch,i) => (
                                                <React.Fragment key={ch}>
                                                  <span style={{ fontSize:"0.68rem",fontWeight:700,
                                                    padding:"1px 7px", borderRadius:20,
                                                    background:CHANNEL_BG[ch], color:CHANNEL_COLOR[ch] }}>
                                                    {ch}
                                                  </span>
                                                  {i < 2 && <span style={{ color:C.subtle, margin:"0 2px" }}>→</span>}
                                                </React.Fragment>
                                              ))}
                                            </span>
                                          </>
                                          : "Send only via selected channel; skip if not available"}
                                      </div>
                                    </div>

                                    {/* Message preview */}
                                    {(tpl || step.message) && (
                                      <div>
                                        <Lbl>Message preview</Lbl>
                                        <MsgPreview step={step}
                                          templates={templates} landingPages={landingPages} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {/* Add step */}
              <button onClick={addStep}
                style={{ width:"100%", marginTop:12, padding:"10px 0", borderRadius:10,
                  border:`2px dashed ${C.border}`, background:"none", cursor:"pointer",
                  fontSize:"0.82rem", fontWeight:700, color:C.subtle,
                  display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=C.brand;e.currentTarget.style.color=C.brand;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.subtle;}}>
                <FaPlus size={11} /> Add Another Step
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ══ RIGHT PANEL — always-live journey map ════════════════════════════ */}
      <div style={{ flex:1, display:"flex", flexDirection:"column",
        background:"#f5f6fa", overflow:"hidden" }}>

        {/* Right header */}
        <div style={{ padding:"14px 20px", borderBottom:`1px solid ${C.border}`,
          background:C.white, flexShrink:0,
          display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ fontSize:"0.78rem", fontWeight:800, color:C.subtle,
            textTransform:"uppercase", letterSpacing:"0.07em" }}>
            Journey Map
          </div>
          <div style={{ display:"flex", gap:16 }}>
            {[
              { label:"Steps",    val:steps.length,                               color:C.brand },
              { label:"Channels", val:[...new Set(steps.map(s=>s.channel))].length, color:C.green },
            ].map(({label,val,color}) => (
              <div key={label} style={{ textAlign:"center" }}>
                <div style={{ fontSize:"1rem", fontWeight:800, color }}>{val}</div>
                <div style={{ fontSize:"0.62rem", color:C.subtle, textTransform:"uppercase",
                  letterSpacing:"0.06em" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Journey flow diagram */}
        <div style={{ flex:1, overflowY:"auto", padding:"24px 20px",
          display:"flex", flexDirection:"column", alignItems:"center" }}>

          {/* Trigger node */}
          <div style={{ padding:"10px 22px", borderRadius:99, fontWeight:700,
            fontSize:"0.84rem", color:C.white, background:trigMeta.color,
            boxShadow:`0 4px 14px ${trigMeta.color}50`,
            display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:"1.1rem" }}>{trigMeta.emoji}</span>
            {trigger} Trigger
          </div>

          {steps.map((step, idx) => {
            const tpl = templates.find(t => String(t.templateid||t.id) === String(step.templateId));
            const lp  = landingPages.find(p => String(p.pageid||p.id) === String(step.landingPageId));
            const hasContent = step.templateId || step.message;

            return (
              <React.Fragment key={step.id}>
                {/* Connector */}
                <div style={{ display:"flex", flexDirection:"column",
                  alignItems:"center", margin:"6px 0" }}>
                  <div style={{ width:2, height:18, background:C.border }} />
                  <div style={{ fontSize:"0.68rem", color:C.subtle, fontWeight:600,
                    padding:"2px 8px", borderRadius:20, background:C.bg,
                    border:`1px solid ${C.border}` }}>
                    {step.offsetDays === 0 ? "same day"
                      : step.offsetDays < 0
                        ? `${Math.abs(step.offsetDays)}d before`
                        : `${step.offsetDays}d after`}
                    {" at "}{step.executionTime}
                  </div>
                  <div style={{ width:2, height:18, background:C.border }} />
                </div>

                {/* Step node */}
                <div style={{ width:"100%", maxWidth:360, borderRadius:12,
                  border:`1.5px solid ${hasContent ? CHANNEL_COLOR[step.channel]+"40" : "#fecaca"}`,
                  borderLeft:`4px solid ${CHANNEL_COLOR[step.channel]}`,
                  background:C.white, overflow:"hidden",
                  boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>

                  {/* Card header */}
                  <div style={{ padding:"8px 12px",
                    background: CHANNEL_BG[step.channel] || C.bg,
                    borderBottom:`1px solid ${C.border}`,
                    display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:22, height:22, borderRadius:"50%",
                      background:CHANNEL_COLOR[step.channel],
                      display:"flex", alignItems:"center", justifyContent:"center",
                      color:C.white, fontSize:"0.65rem", fontWeight:800, flexShrink:0 }}>
                      {idx+1}
                    </div>
                    <span style={{ fontSize:"0.8rem", fontWeight:700, color:C.text }}>
                      Step {idx+1}
                    </span>
                    <span style={{ marginLeft:4, fontSize:"0.72rem", fontWeight:700,
                      padding:"2px 8px", borderRadius:20,
                      background:CHANNEL_COLOR[step.channel]+"20",
                      color:CHANNEL_COLOR[step.channel] }}>
                      {step.channel}
                    </span>
                    {!hasContent && (
                      <span style={{ marginLeft:"auto", fontSize:"0.65rem",
                        color:"#dc2626", fontWeight:700 }}>
                        ⚠ needs template
                      </span>
                    )}
                  </div>

                  {/* Message bubble */}
                  <div style={{ padding:"10px 12px" }}>
                    <MsgPreview step={step} templates={templates} landingPages={landingPages} />
                    {lp && (
                      <div style={{ marginTop:8, fontSize:"0.72rem", color:C.green,
                        display:"flex", alignItems:"center", gap:5 }}>
                        <i className="fas fa-link" style={{ fontSize:"0.62rem" }} />
                        {lp.title}
                      </div>
                    )}
                    {step.channelFallback && (
                      <div style={{ marginTop:6, fontSize:"0.68rem", color:C.muted,
                        display:"flex", alignItems:"center", gap:4 }}>
                        <i className="fas fa-random" style={{ fontSize:"0.6rem" }} />
                        Fallback: WA → SMS → Email
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* End node */}
          <div style={{ marginTop:12, display:"flex", flexDirection:"column",
            alignItems:"center", gap:0 }}>
            <div style={{ width:2, height:18, background:C.border }} />
            <div style={{ padding:"8px 22px", borderRadius:99,
              border:`1.5px solid ${C.border}`, background:C.bg,
              fontSize:"0.8rem", color:C.muted, fontWeight:600 }}>
              ✅ Journey End
            </div>
          </div>

          {/* Save CTA at bottom of map */}
          <div style={{ marginTop:28, width:"100%", maxWidth:360,
            padding:"16px", borderRadius:12,
            background:C.brandLt, border:`1px solid #c7d2fe` }}>
            <div style={{ fontSize:"0.78rem", color:C.brand, fontWeight:700,
              marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
              <i className="fas fa-check-circle" />
              Ready to go?
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={() => save("Draft")} disabled={saving}
                style={{ flex:1, padding:"9px 0", borderRadius:8,
                  border:`1px solid ${C.border}`, background:C.white,
                  color:C.text, fontSize:"0.8rem", fontWeight:600,
                  cursor:"pointer" }}>
                Save Draft
              </button>
              <button onClick={() => save("Active")} disabled={saving}
                style={{ flex:2, padding:"9px 0", borderRadius:8,
                  border:"none", background:C.brand, color:C.white,
                  fontSize:"0.8rem", fontWeight:700, cursor:"pointer",
                  display:"flex", alignItems:"center",
                  justifyContent:"center", gap:6 }}>
                <FaBolt size={11} /> Activate Journey
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Test modal — full-height right panel ── */}
      <SendTestModal
        show={showTest}
        onHide={() => setShowTest(false)}
        journeyName={name}
        steps={steps}
        templates={templates}
        templateChannel={steps[0]?.channel}
      />
    </div>
  );
};

export default JourneyBuilder;
